import {
    ActionIcon,
    Alert,
    Box,
    Button,
    Checkbox,
    Group,
    Menu,
    Paper,
    Radio,
    Select,
    Stack,
    Switch,
    Text,
    Textarea,
    TextInput,
    Timeline,
    Title,
} from '@mantine/core'
import {DateInput} from '@mantine/dates'
import {IconCalendarEvent, IconFlag, IconPlus, IconX} from '@tabler/icons-react'
import {addDays, format} from 'date-fns'
import {Fragment} from 'react'

import {parseDateInputValue} from 'src/jobs/job-schedule-format'
import {JobTimePicker} from 'src/jobs/job-time-picker'
import {useActiveServiceTypes} from 'src/settings/use-service-types'

import {
    addTemplateRequirement,
    DEFAULT_STAGE_JOB_NAMES,
    deriveServiceVisitDates,
    isSeveralDays,
    removeTemplateRequirement,
    resolveStageTimes,
    setStageJobName,
    setStageTimes,
    setTemplateRequirementType,
    SEVERAL_DAYS_LABEL,
    stageJobNameValue,
    updateTemplateRequirementQty,
} from './build-out-logic'
import {BuildOutQuantityStepper} from './build-out-quantity-stepper'
import {
    BUILD_REQUIREMENT_KIND_LABELS,
    type BuildOutScheduling,
    type BuildPattern,
    type GeneratedJob,
    type GeneratedRequirement,
    type ServiceTemplateRequirement,
} from './build-out-types'
import classes from './build-out-ui.module.css'

type BuildStage = GeneratedJob['stage']

export const BUILD_PATTERN_OPTIONS: {value: BuildPattern; label: string; description: string}[] = [
    {
        value: 'one_day',
        label: 'One day',
        description:
            'Crew goes out, does the work, and comes back the same day. Nothing is left on site.',
    },
    {
        value: 'deploy_pickup',
        label: 'Deploy + Pickup',
        description:
            'Equipment is dropped off, stays on site, and is picked up when the work is done.',
    },
    {
        value: 'deploy_service_pickup',
        label: 'Deploy · Service · Pickup',
        description:
            'Equipment stays on site and crew comes back on a repeating schedule to service it.',
    },
]

const SEVERAL_DAYS_OPTION: {value: 'several_days'; label: string; description: string} = {
    value: 'several_days',
    label: SEVERAL_DAYS_LABEL,
    description: 'One job running daily from the start date to the end date.',
}

type PatternChoice = BuildPattern | 'several_days'

function patternChoiceForScheduling(scheduling: Scheduling): PatternChoice {
    return isSeveralDays(scheduling) ? 'several_days' : scheduling.pattern
}

/** Copy the first job's notes and start time across pattern changes. */
function carryFirstJobFields(scheduling: Scheduling, pattern: BuildPattern): Scheduling {
    if (pattern === 'deploy_pickup' && scheduling.pattern !== 'deploy_pickup') {
        return {
            ...scheduling,
            deployNotes: scheduling.deployNotes.trim()
                ? scheduling.deployNotes
                : scheduling.serviceNotes,
            deployStartTime: scheduling.deployStartTime ?? scheduling.startTime ?? null,
        }
    }
    if (pattern === 'one_day' && scheduling.pattern === 'deploy_pickup') {
        return {
            ...scheduling,
            serviceNotes: scheduling.serviceNotes.trim()
                ? scheduling.serviceNotes
                : scheduling.deployNotes,
            startTime: scheduling.startTime ?? scheduling.deployStartTime ?? null,
        }
    }
    return scheduling
}

export function applyPatternChoice(scheduling: Scheduling, choice: PatternChoice): Scheduling {
    scheduling = carryFirstJobFields(scheduling, choice === 'several_days' ? 'one_day' : choice)
    if (choice === 'several_days') {
        return {
            ...scheduling,
            pattern: 'one_day',
            severalDays: true,
            endDate:
                scheduling.endDate > scheduling.startDate
                    ? scheduling.endDate
                    : addDays(scheduling.startDate, 1),
        }
    }
    return {...scheduling, pattern: choice, severalDays: false}
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const STAGE_COLOR: Record<BuildStage, string> = {
    deploy: 'blue',
    service: 'violet',
    pickup: 'teal',
}

const STAGE_DOT: Record<BuildStage, string> = {
    deploy: 'var(--mantine-color-blue-6)',
    service: 'var(--mantine-color-violet-6)',
    pickup: 'var(--mantine-color-teal-6)',
}

const STAGE_PANEL: Record<BuildStage, string> = {
    deploy: classes['jobPanelDeploy']!,
    service: classes['jobPanelService']!,
    pickup: classes['jobPanelPickup']!,
}

type Scheduling = BuildOutScheduling

function requirementTypeName(
    kind: ServiceTemplateRequirement['kind'],
    typeId: string,
    workerTypeNames: Map<string, string>,
    equipmentTypeNames: Map<string, string>,
): string {
    if (!typeId) {
        return `Select ${BUILD_REQUIREMENT_KIND_LABELS[kind].toLowerCase()} type`
    }

    if (kind === 'worker') {
        return workerTypeNames.get(typeId) ?? 'Worker'
    }

    return equipmentTypeNames.get(typeId) ?? 'Equipment'
}

type RequirementChipProps = {
    requirement: GeneratedRequirement
    dotColor: string
    workerTypeNames: Map<string, string>
    equipmentTypeNames: Map<string, string>
    muted?: boolean
}

function RequirementChip({
    requirement,
    dotColor,
    workerTypeNames,
    equipmentTypeNames,
    muted = false,
}: RequirementChipProps) {
    return (
        <Box className={muted ? `${classes['chip']} ${classes['chipMuted']}` : classes['chip']}>
            <span className={classes['chipDot']} style={{backgroundColor: dotColor}} aria-hidden />
            <Text size="sm" span>
                {requirement.qty} ×{' '}
                {requirementTypeName(
                    requirement.kind,
                    requirement.typeId,
                    workerTypeNames,
                    equipmentTypeNames,
                )}
            </Text>
            {!muted && <span className={classes['chipTag']}>Unassigned</span>}
        </Box>
    )
}

type TemplateChipEditorProps = {
    requirements: ServiceTemplateRequirement[]
    workerTypeOptions: {value: string; label: string}[]
    equipmentTypeOptions: {value: string; label: string}[]
    quantityLabel: string
    onChange: (requirements: ServiceTemplateRequirement[]) => void
}

function TemplateChipEditor({
    requirements,
    workerTypeOptions,
    equipmentTypeOptions,
    quantityLabel,
    onChange,
}: TemplateChipEditorProps) {
    return (
        <Box className={classes['reqChips']}>
            {requirements.map((requirement) => (
                <Box key={requirement.id} className={classes['templateChip']}>
                    <span
                        className={classes['chipDot']}
                        style={{backgroundColor: STAGE_DOT.service}}
                        aria-hidden
                    />
                    <Select
                        className={classes['templateSelect']}
                        variant="unstyled"
                        aria-label={`${BUILD_REQUIREMENT_KIND_LABELS[requirement.kind]} type`}
                        placeholder={`Select ${BUILD_REQUIREMENT_KIND_LABELS[
                            requirement.kind
                        ].toLowerCase()}`}
                        data={
                            requirement.kind === 'worker' ? workerTypeOptions : equipmentTypeOptions
                        }
                        value={requirement.typeId || null}
                        onChange={(value) => {
                            if (value == null) {
                                return
                            }

                            onChange(
                                setTemplateRequirementType(requirements, requirement.id, value),
                            )
                        }}
                        searchable
                        comboboxProps={{withinPortal: true}}
                    />
                    <BuildOutQuantityStepper
                        value={requirement.qty}
                        ariaLabel={quantityLabel}
                        onChange={(qty) =>
                            onChange(
                                updateTemplateRequirementQty(requirements, requirement.id, qty),
                            )
                        }
                    />
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        aria-label="Remove requirement"
                        onClick={() =>
                            onChange(removeTemplateRequirement(requirements, requirement.id))
                        }
                    >
                        <IconX size={14} />
                    </ActionIcon>
                </Box>
            ))}

            <Menu withinPortal position="bottom-start">
                <Menu.Target>
                    <Button
                        className={classes['addChip']}
                        variant="default"
                        size="xs"
                        radius="xl"
                        leftSection={<IconPlus size={14} />}
                    >
                        Add requirement
                    </Button>
                </Menu.Target>
                <Menu.Dropdown>
                    <Menu.Item
                        onClick={() => onChange(addTemplateRequirement(requirements, 'worker'))}
                    >
                        Worker
                    </Menu.Item>
                    <Menu.Item
                        onClick={() => onChange(addTemplateRequirement(requirements, 'equipment'))}
                    >
                        Equipment
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>
        </Box>
    )
}

/** The notes control a job shows: its own editable field, or the stamp taken from the first visit. */
type JobNotes =
    | {
          editable: true
          label: string
          description: string
          placeholder: string
          value: string
          apply: (scheduling: Scheduling, value: string) => Scheduling
      }
    | {editable: false; label: string; value: string}

function jobNotes(job: GeneratedJob, scheduling: Scheduling): JobNotes {
    if (scheduling.pattern === 'one_day') {
        return {
            editable: true,
            label: 'Job notes',
            description: 'Shared with drivers on this job.',
            placeholder: 'What the crew needs to know — access, the work itself, paperwork',
            value: scheduling.serviceNotes,
            apply: (current, value) => ({...current, serviceNotes: value}),
        }
    }

    if (job.stage === 'deploy') {
        return {
            editable: true,
            label: 'Deployment notes',
            description: 'Shared with drivers on this job.',
            placeholder: 'What the deploy crew needs to know — access, staging, paperwork',
            value: scheduling.deployNotes,
            apply: (current, value) => ({...current, deployNotes: value}),
        }
    }

    if (job.stage === 'pickup') {
        return {
            editable: true,
            label: 'Pickup notes',
            description: 'Shared with drivers on this job.',
            placeholder: 'What the pickup crew needs to know — rinse-out, final readings, photos',
            value: scheduling.pickupNotes,
            apply: (current, value) => ({...current, pickupNotes: value}),
        }
    }

    if ((job.visitIndex ?? 0) > 0) {
        return {
            editable: false,
            label: 'Service notes (carried over from the first service visit)',
            value: scheduling.serviceNotes,
        }
    }

    return {
        editable: true,
        label: 'Service notes — applies to every service visit',
        description: 'Shared with drivers on this job.',
        placeholder: 'Standing instructions for every service visit',
        value: scheduling.serviceNotes,
        apply: (current, value) => ({...current, serviceNotes: value}),
    }
}

/** The name and time fields a stage owns, shown once and reused by every job of that stage. */
type StageFieldsProps = {
    stage: BuildStage
    scheduling: Scheduling
    onSchedulingChange: (scheduling: Scheduling) => void
    timeDisabled: boolean
    jobNameError?: React.ReactNode
}

function StageFields({
    stage,
    scheduling,
    onSchedulingChange,
    timeDisabled,
    jobNameError,
}: StageFieldsProps) {
    // Service schedules name their own jobs server-side, so names are not editable for that pattern.
    const nameDisabled = scheduling.pattern === 'deploy_service_pickup'
    const times = resolveStageTimes(scheduling, stage)
    const isOneDay = scheduling.pattern === 'one_day'
    const stageLabel = isOneDay ? 'Job' : DEFAULT_STAGE_JOB_NAMES[stage]
    const nameLabel = isOneDay ? 'Job name' : `${stageLabel} name`
    const nameDescription = nameDisabled
        ? 'Set by the schedule'
        : // Deploy and pickup each produce a single job; only service visits repeat.
          stage === 'service'
          ? 'Used for every service visit'
          : undefined

    return (
        <Group align="stretch" gap="sm" wrap="wrap" mb="sm">
            <TextInput
                label={nameLabel}
                description={nameDescription}
                placeholder={isOneDay ? 'Name this job' : DEFAULT_STAGE_JOB_NAMES[stage]}
                value={
                    nameDisabled
                        ? DEFAULT_STAGE_JOB_NAMES[stage]
                        : stageJobNameValue(scheduling, stage)
                }
                disabled={nameDisabled}
                error={jobNameError}
                onChange={(event) =>
                    onSchedulingChange(
                        setStageJobName(scheduling, stage, event.currentTarget.value),
                    )
                }
                style={{flex: 1, minWidth: 220}}
            />
            <JobTimePicker
                label="Start time"
                description={nameDescription ? ' ' : undefined}
                aria-label={`${stageLabel} start time`}
                value={times.startTime}
                disabled={timeDisabled}
                onChange={(startTime) =>
                    onSchedulingChange(setStageTimes(scheduling, stage, {startTime}))
                }
            />
        </Group>
    )
}

export type BuildOutTimelineProps = {
    jobs: GeneratedJob[]
    workerTypeNames: Map<string, string>
    equipmentTypeNames: Map<string, string>
    workerTypeOptions: {value: string; label: string}[]
    equipmentTypeOptions: {value: string; label: string}[]
    scheduling: Scheduling
    onSchedulingChange: (scheduling: Scheduling) => void
    onToggleSkip?: (job: GeneratedJob, skipped: boolean) => void
    timeDisabled?: boolean
    /** Error for the name field of the job the host validates — the one-day job. */
    jobNameError?: React.ReactNode
    pickupJobNameError?: React.ReactNode
}

export function BuildOutTimeline({
    jobs,
    workerTypeNames,
    equipmentTypeNames,
    workerTypeOptions,
    equipmentTypeOptions,
    scheduling,
    onSchedulingChange,
    onToggleSkip,
    timeDisabled = false,
    jobNameError,
    pickupJobNameError,
}: BuildOutTimelineProps) {
    const {data: serviceTypes = []} = useActiveServiceTypes()
    const serviceTypeName =
        serviceTypes.find((type) => type.id === scheduling.serviceTypeId)?.name ?? null

    return (
        <Timeline active={jobs.length} bulletSize={18} lineWidth={2}>
            {jobs.map((job) => {
                const isTemplateVisit = job.stage === 'service' && job.visitIndex === 0
                const isMirrorVisit = job.stage === 'service' && (job.visitIndex ?? 0) > 0
                const notes = jobNotes(job, scheduling)

                return (
                    <Timeline.Item key={job.id} color={STAGE_COLOR[job.stage]}>
                        <Paper
                            className={
                                job.skipped
                                    ? `${classes['jobCard']} ${classes['jobSkipped']}`
                                    : classes['jobCard']
                            }
                        >
                            <Box className={classes['jobGrid']}>
                                <Box className={`${classes['jobPanel']} ${STAGE_PANEL[job.stage]}`}>
                                    <Text className={classes['jobLabel']}>{job.label}</Text>
                                    <Text className={classes['jobDate']}>
                                        {job.stage === 'pickup' &&
                                        scheduling.pattern === 'deploy_pickup' &&
                                        scheduling.pickupDateTbc === true
                                            ? 'Date TBC'
                                            : `${format(job.date, 'EEE, MMM d')}${
                                                  job.endDate
                                                      ? ` – ${format(job.endDate, 'EEE, MMM d')}`
                                                      : ''
                                              }`}
                                    </Text>
                                    {job.stage === 'service' && serviceTypeName != null && (
                                        <span className={classes['jobServiceType']}>
                                            {serviceTypeName}
                                        </span>
                                    )}
                                    {isTemplateVisit && (
                                        <span className={classes['jobFlag']}>
                                            <IconFlag size={11} /> Applies to all
                                        </span>
                                    )}
                                    {isMirrorVisit && (
                                        <span className={classes['jobFlag']}>
                                            <IconFlag size={11} /> Same as first visit
                                        </span>
                                    )}
                                    {job.stage === 'service' && onToggleSkip != null && (
                                        <Switch
                                            className={classes['jobSkip']}
                                            size="sm"
                                            label="Skip this job"
                                            checked={job.skipped ?? false}
                                            onChange={(event) =>
                                                onToggleSkip(job, event.currentTarget.checked)
                                            }
                                        />
                                    )}
                                </Box>

                                <Box className={classes['jobReqs']}>
                                    {!isMirrorVisit &&
                                        !(
                                            scheduling.pattern === 'deploy_service_pickup' &&
                                            timeDisabled
                                        ) && (
                                            <StageFields
                                                stage={job.stage}
                                                scheduling={scheduling}
                                                onSchedulingChange={onSchedulingChange}
                                                timeDisabled={timeDisabled}
                                                jobNameError={
                                                    job.stage === 'deploy'
                                                        ? jobNameError
                                                        : job.stage === 'pickup'
                                                          ? pickupJobNameError
                                                          : undefined
                                                }
                                            />
                                        )}

                                    <Box className={classes['jobNotes']}>
                                        {notes.editable ? (
                                            <Textarea
                                                label={notes.label}
                                                description={notes.description}
                                                placeholder={notes.placeholder}
                                                value={notes.value}
                                                onChange={(event) =>
                                                    onSchedulingChange(
                                                        notes.apply(
                                                            scheduling,
                                                            event.currentTarget.value,
                                                        ),
                                                    )
                                                }
                                                minRows={2}
                                                autosize
                                            />
                                        ) : (
                                            <>
                                                <Text className={classes['eyebrow']} mb={6}>
                                                    {notes.label}
                                                </Text>
                                                <Box className={classes['noteReadOnly']}>
                                                    {notes.value || 'No service notes yet'}
                                                </Box>
                                            </>
                                        )}
                                    </Box>

                                    {isTemplateVisit ? (
                                        <>
                                            <Text
                                                className={classes['eyebrow']}
                                                style={{color: 'var(--mantine-color-violet-7)'}}
                                            >
                                                Set the visit — applies to every service visit
                                            </Text>
                                            <TemplateChipEditor
                                                requirements={scheduling.serviceTemplate}
                                                workerTypeOptions={workerTypeOptions}
                                                equipmentTypeOptions={equipmentTypeOptions}
                                                quantityLabel="Service template quantity"
                                                onChange={(serviceTemplate) =>
                                                    onSchedulingChange({
                                                        ...scheduling,
                                                        serviceTemplate,
                                                    })
                                                }
                                            />
                                        </>
                                    ) : isMirrorVisit ? (
                                        job.requirements.length === 0 ? (
                                            <Text size="sm" c="dimmed">
                                                No requirements yet
                                            </Text>
                                        ) : (
                                            <Box className={classes['reqChips']}>
                                                {job.requirements.map((requirement, index) => (
                                                    <RequirementChip
                                                        key={`${job.id}-${index}`}
                                                        requirement={requirement}
                                                        dotColor={STAGE_DOT.service}
                                                        workerTypeNames={workerTypeNames}
                                                        equipmentTypeNames={equipmentTypeNames}
                                                        muted
                                                    />
                                                ))}
                                            </Box>
                                        )
                                    ) : (
                                        <>
                                            <Text className={classes['eyebrow']}>
                                                Ops will assign
                                            </Text>
                                            {job.requirements.length === 0 ? (
                                                <Text size="sm" c="dimmed">
                                                    No requirements yet
                                                </Text>
                                            ) : (
                                                <Box className={classes['reqChips']}>
                                                    {job.requirements.map((requirement, index) => (
                                                        <RequirementChip
                                                            key={`${job.id}-${index}`}
                                                            requirement={requirement}
                                                            dotColor={STAGE_DOT[job.stage]}
                                                            workerTypeNames={workerTypeNames}
                                                            equipmentTypeNames={equipmentTypeNames}
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </Paper>
                    </Timeline.Item>
                )
            })}
        </Timeline>
    )
}

/** Illustrative stage sequence shown on each pattern card — not the real job list. */
const PATTERN_DIAGRAM_STAGES: Record<PatternChoice, BuildStage[]> = {
    one_day: ['deploy'],
    several_days: ['deploy', 'deploy', 'deploy'],
    deploy_pickup: ['deploy', 'pickup'],
    deploy_service_pickup: ['deploy', 'service', 'service', 'service', 'pickup'],
}

function PatternDiagram({pattern}: {pattern: PatternChoice}) {
    return (
        <span className={classes['patternDiagram']} aria-hidden>
            {PATTERN_DIAGRAM_STAGES[pattern].map((stage, index) => (
                <Fragment key={`${stage}-${index}`}>
                    {index > 0 && <span className={classes['patternLine']} />}
                    <span
                        className={`${classes['patternDot']} ${
                            stage === 'service' ? classes['patternDotSmall'] : ''
                        }`}
                        style={{backgroundColor: STAGE_DOT[stage]}}
                    />
                </Fragment>
            ))}
        </span>
    )
}

/** How many jobs the given pattern would generate for the current dates and cadence. */
function patternJobCount(pattern: PatternChoice, scheduling: Scheduling): number {
    if (pattern === 'one_day' || pattern === 'several_days') {
        return 1
    }

    if (pattern === 'deploy_pickup') {
        return 2
    }

    const visits = deriveServiceVisitDates(
        scheduling.startDate,
        scheduling.endDate,
        scheduling.cadence,
        {visitsOnly: scheduling.visitsOnly},
    ).length
    return scheduling.visitsOnly ? visits : 2 + visits
}

export type BuildPatternPickerProps = {
    scheduling: Scheduling
    onSchedulingChange: (scheduling: Scheduling) => void
    disabled?: boolean
    /** Include the several-days pattern option. */
    allowSeveralDays?: boolean
}

export function BuildPatternPicker({
    scheduling,
    onSchedulingChange,
    disabled = false,
    allowSeveralDays = false,
}: BuildPatternPickerProps) {
    const options: {value: PatternChoice; label: string; description: string}[] = allowSeveralDays
        ? [BUILD_PATTERN_OPTIONS[0]!, SEVERAL_DAYS_OPTION, ...BUILD_PATTERN_OPTIONS.slice(1)]
        : BUILD_PATTERN_OPTIONS
    return (
        <Radio.Group
            aria-label="Build pattern"
            value={patternChoiceForScheduling(scheduling)}
            onChange={(value) =>
                onSchedulingChange(applyPatternChoice(scheduling, value as PatternChoice))
            }
        >
            <Box className={classes['patternGrid']}>
                {options.map((option) => {
                    const jobCount = patternJobCount(option.value, scheduling)

                    return (
                        <Radio.Card
                            key={option.value}
                            className={classes['patternCard']}
                            value={option.value}
                            aria-label={option.label}
                            disabled={disabled}
                            withBorder={false}
                        >
                            <span className={classes['patternCardInner']}>
                                <span className={classes['patternTitle']}>{option.label}</span>
                                <span className={classes['patternDescription']}>
                                    {option.description}
                                </span>
                                <PatternDiagram pattern={option.value} />
                                <span className={classes['patternCount']}>
                                    {jobCount} {jobCount === 1 ? 'job' : 'jobs'}
                                </span>
                            </span>
                        </Radio.Card>
                    )
                })}
            </Box>
        </Radio.Group>
    )
}

export type JobsReadySwitchProps = {
    scheduling: Scheduling
    onSchedulingChange: (scheduling: Scheduling) => void
}

/** Dispatch readiness for the jobs about to be created — rendered next to the host's submit action. */
export function JobsReadySwitch({scheduling, onSchedulingChange}: JobsReadySwitchProps) {
    return (
        <Switch
            label="Mark jobs as ready for dispatch"
            checked={scheduling.markJobsReadyForDispatch ?? true}
            onChange={(event) =>
                onSchedulingChange({
                    ...scheduling,
                    markJobsReadyForDispatch: event.currentTarget.checked,
                })
            }
        />
    )
}

export type ScheduleBuildOutControlsProps = {
    scheduling: Scheduling
    onSchedulingChange: (scheduling: Scheduling) => void
    patternDisabled?: boolean
    cadenceDisabled?: boolean
    serviceTypeError?: React.ReactNode
    allowSeveralDays?: boolean
    endDateError?: React.ReactNode
}

export function ScheduleBuildOutControls({
    scheduling,
    onSchedulingChange,
    patternDisabled = false,
    cadenceDisabled = false,
    serviceTypeError,
    allowSeveralDays = false,
    endDateError,
}: ScheduleBuildOutControlsProps) {
    const {data: serviceTypes = [], isLoading: isLoadingServiceTypes} = useActiveServiceTypes()
    const serviceVisitCount = deriveServiceVisitDates(
        scheduling.startDate,
        scheduling.endDate,
        scheduling.cadence,
        {visitsOnly: scheduling.visitsOnly},
    ).length
    const weekdaysLabel = [...scheduling.cadence.weekdays]
        .sort((a, b) => a - b)
        .map((day) => WEEKDAY_LABELS[day])
        .join(' / ')
    const visitLabel = serviceVisitCount === 1 ? 'visit' : 'visits'

    if (!isLoadingServiceTypes && serviceTypes.length === 0) {
        return (
            <Paper withBorder radius="md" p="lg">
                <Alert color="yellow" title="Work categories are not configured">
                    An admin must add active work categories in Settings → Work categories before
                    this build-out can be scheduled.
                </Alert>
            </Paper>
        )
    }

    return (
        <Stack gap="lg">
            <Box>
                <Title order={3}>Job schedule</Title>
                <Text size="sm" c="dimmed">
                    {patternDisabled
                        ? 'Adjust dates and service settings.'
                        : 'Set the type of schedule and the start and end dates.'}
                </Text>
            </Box>

            {!patternDisabled ? (
                <BuildPatternPicker
                    scheduling={scheduling}
                    onSchedulingChange={onSchedulingChange}
                    disabled={patternDisabled}
                    allowSeveralDays={allowSeveralDays}
                />
            ) : null}

            <Box>
                <Box className={classes['scheduleRow']}>
                    <Box className={classes['scheduleField']}>
                        <Text className={classes['eyebrow']}>Start</Text>
                        <DateInput
                            aria-label="Start date"
                            value={scheduling.startDate}
                            disabled={patternDisabled}
                            onChange={(value) => {
                                const startDate = parseDateInputValue(value)
                                if (startDate == null) {
                                    return
                                }

                                const endDate =
                                    startDate > scheduling.endDate ? startDate : scheduling.endDate

                                onSchedulingChange({...scheduling, startDate, endDate})
                            }}
                        />
                    </Box>

                    {(scheduling.pattern !== 'one_day' || isSeveralDays(scheduling)) && (
                        <Box className={classes['scheduleField']}>
                            <Text className={classes['eyebrow']}>End</Text>
                            <DateInput
                                aria-label="End date"
                                value={scheduling.endDate}
                                error={endDateError}
                                disabled={
                                    scheduling.pattern === 'deploy_pickup' &&
                                    scheduling.pickupDateTbc === true
                                }
                                onChange={(value) => {
                                    const endDate = parseDateInputValue(value)
                                    if (endDate == null) {
                                        return
                                    }

                                    const clampedEnd =
                                        endDate < scheduling.startDate
                                            ? scheduling.startDate
                                            : endDate

                                    onSchedulingChange({...scheduling, endDate: clampedEnd})
                                }}
                            />
                            {scheduling.pattern === 'deploy_pickup' && (
                                <Checkbox
                                    mt="xs"
                                    size="xs"
                                    label="Pickup date TBC"
                                    description="Shown to dispatch until a date is set"
                                    checked={scheduling.pickupDateTbc === true}
                                    onChange={(event) =>
                                        onSchedulingChange({
                                            ...scheduling,
                                            pickupDateTbc: event.currentTarget.checked,
                                        })
                                    }
                                />
                            )}
                        </Box>
                    )}

                    <Box className={classes['scheduleDivider']} aria-hidden />

                    <Box className={classes['scheduleField']}>
                        <Group gap="xs" align="center">
                            <Text
                                className={classes['eyebrow']}
                                style={{color: 'var(--mantine-color-violet-7)'}}
                            >
                                Category
                            </Text>
                            <span className={classes['fieldTag']}>From settings</span>
                        </Group>
                        <Select
                            aria-label="Category"
                            placeholder="Select category"
                            w={230}
                            data={serviceTypes.map((type) => ({value: type.id, label: type.name}))}
                            value={scheduling.serviceTypeId}
                            onChange={(value) =>
                                onSchedulingChange({...scheduling, serviceTypeId: value})
                            }
                            searchable
                            comboboxProps={{withinPortal: true}}
                            disabled={isLoadingServiceTypes}
                            error={serviceTypeError}
                        />
                    </Box>
                </Box>
            </Box>

            {scheduling.pattern === 'deploy_service_pickup' && (
                <>
                    <Box className={classes['cadenceBar']}>
                        <span className={classes['cadenceIcon']} aria-hidden>
                            <IconCalendarEvent size={20} />
                        </span>
                        <Text size="sm" fw={600}>
                            Service between deploy &amp; pickup
                        </Text>
                        <Box
                            role="group"
                            aria-label="Service weekday"
                            className={classes['weekdayGroup']}
                        >
                            {WEEKDAY_LABELS.map((label, index) => {
                                const selected = scheduling.cadence.weekdays.includes(index)

                                return (
                                    <button
                                        key={`${label}-${index}`}
                                        type="button"
                                        aria-label={label}
                                        aria-pressed={selected}
                                        disabled={cadenceDisabled}
                                        className={`${classes['weekday']} ${
                                            selected ? classes['weekdaySelected'] : ''
                                        }`}
                                        onClick={() => {
                                            const weekdays = scheduling.cadence.weekdays.includes(
                                                index,
                                            )
                                                ? scheduling.cadence.weekdays.filter(
                                                      (day) => day !== index,
                                                  )
                                                : [...scheduling.cadence.weekdays, index].sort(
                                                      (a, b) => a - b,
                                                  )

                                            onSchedulingChange({
                                                ...scheduling,
                                                cadence: {...scheduling.cadence, weekdays},
                                            })
                                        }}
                                    >
                                        {label.charAt(0)}
                                    </button>
                                )
                            })}
                        </Box>
                        <Text className={classes['cadenceSummary']}>
                            Weekly · every {weekdaysLabel || '—'} · {serviceVisitCount} {visitLabel}
                        </Text>
                    </Box>
                </>
            )}
        </Stack>
    )
}

export type BuildOutSummaryStatsProps = {
    jobCount: number
    unitCount: number
    crewCount: number
}

export function BuildOutSummaryStats({jobCount, unitCount, crewCount}: BuildOutSummaryStatsProps) {
    const unitLabel = unitCount === 1 ? 'unit' : 'units'
    const jobLabel = jobCount === 1 ? 'job' : 'jobs'

    return (
        <Group
            className={classes['summary']}
            role="group"
            aria-label={`Build-out summary: ${jobCount} ${jobLabel}, ${unitCount} ${unitLabel}, ${crewCount} crew`}
        >
            <Box className={classes['stat']}>
                <span className={classes['statValue']}>{jobCount}</span>
                <span className={classes['statLabel']}>{jobLabel}</span>
            </Box>
            <Box className={classes['stat']}>
                <span className={classes['statValue']}>{unitCount}</span>
                <span className={classes['statLabel']}>{unitLabel}</span>
            </Box>
            <Box className={classes['stat']}>
                <span className={classes['statValue']}>{crewCount}</span>
                <span className={classes['statLabel']}>crew</span>
            </Box>
        </Group>
    )
}

export type ScheduleBuildOutPreviewProps = {
    jobs: GeneratedJob[]
    workerTypeNames: Map<string, string>
    equipmentTypeNames: Map<string, string>
    workerTypeOptions: {value: string; label: string}[]
    equipmentTypeOptions: {value: string; label: string}[]
    scheduling: Scheduling
    onSchedulingChange: (scheduling: Scheduling) => void
    onToggleSkip?: (job: GeneratedJob, skipped: boolean) => void
    timeDisabled?: boolean
    jobNameError?: React.ReactNode
    pickupJobNameError?: React.ReactNode
}

export function ScheduleBuildOutPreview({
    jobs,
    workerTypeNames,
    equipmentTypeNames,
    workerTypeOptions,
    equipmentTypeOptions,
    scheduling,
    onSchedulingChange,
    onToggleSkip,
    timeDisabled,
    jobNameError,
    pickupJobNameError,
}: ScheduleBuildOutPreviewProps) {
    return (
        <Stack gap="md">
            <Box className={classes['buildoutHeader']}>
                <Group gap="xs" align="baseline">
                    <Title order={3}>The build-out</Title>
                    <Text size="sm" c="dimmed">
                        · {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'}
                    </Text>
                </Group>
                <Text size="sm" c="dimmed">
                    Each job carries its requirements and its notes — Ops assigns the driver, unit
                    &amp; stock before the date.
                </Text>
            </Box>

            <BuildOutTimeline
                jobs={jobs}
                workerTypeNames={workerTypeNames}
                equipmentTypeNames={equipmentTypeNames}
                workerTypeOptions={workerTypeOptions}
                equipmentTypeOptions={equipmentTypeOptions}
                scheduling={scheduling}
                onSchedulingChange={onSchedulingChange}
                onToggleSkip={onToggleSkip}
                timeDisabled={timeDisabled}
                jobNameError={jobNameError}
                pickupJobNameError={pickupJobNameError}
            />
        </Stack>
    )
}

export type ScheduleBuildOutEditorProps = ScheduleBuildOutControlsProps &
    ScheduleBuildOutPreviewProps

/** Host-agnostic scheduling controls and build-out timeline preview. */
export function ScheduleBuildOutEditor({
    scheduling,
    onSchedulingChange,
    patternDisabled,
    cadenceDisabled,
    serviceTypeError,
    timeDisabled,
    jobNameError,
    pickupJobNameError,
    allowSeveralDays,
    endDateError,
    jobs,
    workerTypeNames,
    equipmentTypeNames,
    workerTypeOptions,
    equipmentTypeOptions,
    onToggleSkip,
}: ScheduleBuildOutEditorProps) {
    return (
        <Stack gap="lg">
            <ScheduleBuildOutControls
                scheduling={scheduling}
                onSchedulingChange={onSchedulingChange}
                patternDisabled={patternDisabled}
                cadenceDisabled={cadenceDisabled}
                serviceTypeError={serviceTypeError}
                allowSeveralDays={allowSeveralDays}
                endDateError={endDateError}
            />
            <ScheduleBuildOutPreview
                jobs={jobs}
                workerTypeNames={workerTypeNames}
                equipmentTypeNames={equipmentTypeNames}
                workerTypeOptions={workerTypeOptions}
                equipmentTypeOptions={equipmentTypeOptions}
                scheduling={scheduling}
                onSchedulingChange={onSchedulingChange}
                onToggleSkip={onToggleSkip}
                timeDisabled={timeDisabled}
                jobNameError={jobNameError}
                pickupJobNameError={pickupJobNameError}
            />
        </Stack>
    )
}
